import { TradeBookingRow } from '@/helpers/portalTradesHelper';
import useContextMenu from '@/utils/hooks/useContextMenu';
import useLanguage from '@/utils/hooks/useLanguage';
import usePortalTradesTable from '@/utils/hooks/usePortalTradesTable';
import { useTableDisclosure } from '@/utils/hooks/useTableDisclosure';
import { TradesFilters } from '@/XplorPortal/PortalTrades';
import { useCallback, useMemo, useState } from 'react';
import CurrentTradeDate from '../common/CurrentTradeDate';
import { TradesTableFilters } from '../table/filters/TradesTableFilters';
import PortalVirtualizedTable, { PortalTableGeneralProps } from '../table/PortalVirtualizedTable';
import TableControls from '../table/TableControls';
import TablePagination from '../table/TablePagination';

export interface PortalDepositRow {}

export interface PortalTradesTableProps extends PortalTableGeneralProps {
    data: TradeBookingRow[];
    filters: TradesFilters;
    setFilters: (filters: TradesFilters) => void;
}

export type PositionFilter = 'Show All' | 'Hide Closed Positions';

const PortalTradesTable = (props: PortalTradesTableProps) => {
    const { data, isLoading } = props;

    const lang = useLanguage();
    const [selectedRow, setSelectedRow] = useState<PortalDepositRow>();

    const contextMenu = useContextMenu();

    const { filterDisclosure, viewDisclosure } = useTableDisclosure({ setSelectedRow });

    const onContextMenu = useCallback(
        (event, request) => {
            setSelectedRow(request);
            contextMenu.handleOpenContextMenu(event);
        },
        [contextMenu]
    );

    const { debouncedTableInstance, globalFilter } = usePortalTradesTable(props);

    const tableHeaders = useMemo(() => {
        return debouncedTableInstance.getLeafHeaders().map((header) => ({
            id: header.id,
            label: header.column.columnDef.header
        }));
    }, [debouncedTableInstance, isLoading]);

    const excelTradeBookingData = useMemo(() => {
        if (data && tableHeaders.length > 0) {
            return data.map((tradeBooking) => {
                return tableHeaders.map((header) => {
                    switch (header.id) {
                        case 'tableExecutionTime':
                            return tradeBooking[header.id].replace(',', '');
                        case 'tableAmount':
                            return tradeBooking.amount;
                        case 'tableContraAmount':
                            return tradeBooking.contraAmount;
                        case 'tablePrice':
                            return tradeBooking.price;
                        default:
                            return tradeBooking[header.id];
                    }
                });
            });
        } else return [];
    }, [data, tableHeaders]);

    const excelData = useMemo(() => {
        return {
            data: excelTradeBookingData,
            fileName: 'Trade-Booking',
            headerData: tableHeaders.map((header) => header.label || '')
        };
    }, [tableHeaders, excelTradeBookingData]);

    return (
        <div className="flex flex-col gap-1 bg-brand-background-dark p-6 py-4 pb-6 border border-brand-neutral-700 text-brand-neutral-200 rounded-md overflow-hidden">
            <div className="flex flex-col h-full gap-4">
                <div className="flex flex-col gap-2">
                    <div className="flex justify-between items-center flex-wrap gap-2 sm:gap-5">
                        <div className="">{lang.portal.recentTrades}</div>
                        {/* <div className="text-lg font-semibold text-neutral-200">{props.title}</div> */}
                        <TableControls
                            excelData={excelData}
                            filterDisclosure={filterDisclosure}
                            mutate={props.mutate}
                            isLoading={props.isLoading}
                        />
                    </div>
                    <TradesTableFilters
                        {...props}
                        opened={filterDisclosure[0]}
                        globalFilter={globalFilter}
                        setGlobalFilter={debouncedTableInstance.setGlobalFilter}
                        resetPageNumber={debouncedTableInstance.setPageIndex}
                    />
                </div>
                <PortalVirtualizedTable tableInstance={debouncedTableInstance} isLoadingTableContent={isLoading} />
                <div className="flex flex-wrap-reverse gap-2 items-center justify-between">
                    <CurrentTradeDate />
                    <TablePagination tableInstance={debouncedTableInstance} />
                </div>
            </div>
        </div>
    );
};

export default PortalTradesTable;
